import moment from 'moment';
import PushStatus from './enums/pushStatus';

/**
 * Single segment returned from the segment list endpoint
 */
export interface SegmentOverviewResult {
  attributes: {
    attributeId: number;
    customers: number;
    name: string;
    columnName: string;
    filterType: SegmentAttributeFilterType;
    selectedFilters: string;
  }[];
  clientId: number;
  clientName: string;
  createdAt: string;
  lastModifiedDate: string;
  createdById: string;
  createdByName: string;
  totalCustomers: number;
  description: string;
  id: number;
  isEditable: boolean;
  isShared: boolean;
  name: string;
  isDependency: boolean;
}

/**
 * Response returned from the segment list endpoint
 */
export interface SegmentOverview {
  results: SegmentOverviewResult[];
  totalItems: number;
}

/**
 * Filter parameters available on the user interface segments list
 */
export interface SegmentFilters {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  min: number | '';
  max: number | '';
  /**
   * isShared: booleans treated as strings to reflect the possible string values
   * usable as query parameters and for use as string ids in the sharing options
   * list
   **/
  isShared: Array<'true' | 'false'>;
  users: string[];
}

/**
 * Filter parameters available on the admin interface segments list
 */
export interface SegmentFiltersAdmin extends SegmentFilters {
  teams: string[];
}

/**
 * A group of available attributes for creating segments, returned in named categories from {client}/attributes endpoint
 */
export interface SegmentAttributeCategory {
  category: string;
  attributes: SegmentAttributeBase[];
}

export type SegmentAttributeFilterType = 'options' | 'range' | 'datetime';

export interface SegmentAttributeBase {
  id: number;
  columnName: string;
  name: string;
  populatedPercentage: number;
  filterType: SegmentAttributeFilterType;
  isTransactional?: boolean;
}

/**
 * Detailed attribute response from the {client}/attributes/{attribute} endpoint with JSON string filters
 */
export interface SegmentAttributeDetailResponse extends SegmentAttributeBase {
  /** JSON string */
  filters: string;
}

export interface SegmentFilterOption {
  value: string | number;
  customers: number;
}

/**
 * Options type attribute with parsed filter details
 */
export interface SegmentAttributeOptions extends SegmentAttributeBase {
  filterType: 'options';
  filters: SegmentFilterOption[];
}

/**
 * Numerical range type attribute with parsed filter details
 */
export interface SegmentAttributeRange extends SegmentAttributeBase {
  filterType: 'range';
  filters: {
    min: number;
    max: number;
    histogram: {
      customers: number;
      max: number;
      min: number;
    }[];
  };
}

/**
 * Date range type attribute with parsed filter details
 */
export interface SegmentAttributeDatetime extends SegmentAttributeBase {
  filterType: 'datetime';
  filters: {
    min: moment.Moment;
    max: moment.Moment;
    histogram: {
      customers: number;
      max: string;
      min: string;
    }[];
  };
}

/**
 * Transactional type attribute with parsed filter details
 */
export interface SegmentAttributeTransactional extends SegmentAttributeBase {
  isTransactional: true;
  filters: {
    where: string;
    operator: SegmentAttributeOperator;
    predicates: SelectedAttribute[];
  };
  transactionalFilters: TransactionalFilter[];
}

export type TransactionalFilter = {
  columnName: string;
  filterId: number;
  filterType: string;
  filters: any;
  name: string;
  filterOperator: SegmentFilterOperator;
};

export enum SegmentAttributeOperator {
  And = 0,
  Or = 1
}

export enum SegmentFilterOperator {
  Is = 0,
  'Is Not' = 1,
  'Begins With' = 2,
  'Does Not Begin With' = 3,
  'Ends With' = 4,
  'Does Not End With' = 5,
  'Contains' = 6,
  'Does Not Contain' = 7,
  'Where' = 8
}

export enum SegmentAttributeFilterRender {
  Input,
  Options,
  Range,
  Datetime,
  Transactional
}

export interface SelectedAttributeBase {
  attributeId: number;
  columnName?: string;
  filterOperator: SegmentFilterOperator;
  isTransactional?: boolean;
  filterId?: number;
  groupBy?: GroupByFilter;
}

export interface SelectedAttributeOption extends SelectedAttributeBase {
  options: string[];
}

export interface SelectedAttributeInput extends SelectedAttributeBase {
  searchText: string;
}

export interface SelectedAttributeRange extends SelectedAttributeBase {
  range: {
    min: number;
    max: number;
  };
}

export interface SelectedAttributeDaterange extends SelectedAttributeBase {
  dateRange: {
    min: string;
    max: string;
  };
}

export interface SelectedAttributeDaysRange extends SelectedAttributeBase {
  numberOfDays: number;
}

export interface SelectedAttributeTransactional extends SelectedAttributeBase {
  where?: string;
  transactionalFilters?: {
    operator: SegmentAttributeOperator;
    predicates: SelectedAttribute[];
  };
}

/**
 * Segment configuration objects defining the chosen attributes and their filter values
 */
export type SelectedAttribute =
  | SelectedAttributeOption
  | SelectedAttributeRange
  | SelectedAttributeDaterange
  | SelectedAttributeDaysRange
  | SelectedAttributeInput
  | SelectedAttributeTransactional;

/**
 * Definition of the attributes property of a segment definition
 */
export interface SegmentAttributeGroup {
  operator: SegmentAttributeOperator;
  predicates: (SelectedAttribute | SegmentAttributeGroup)[];
}

/**
 * Include and Exclude SegmentAttributeGroup pair
 */
export interface SegmentAttributeGroupPair {
  include: SegmentAttributeGroup;
  exclude: SegmentAttributeGroup;
}

/**
 * Segment configuration, body of endpoint calls for creating and updating segments
 */
export interface SegmentConfig {
  name: string;
  description: string;
  isShared: boolean;
  attributes: SegmentAttributeGroupPair;
  composition?: SegmentComposition;
}

/**
 * Returned value from querying Snowflake for the customer count results of a segment
 *
 * `perAttributeGroup` customer counts are returned for attributes or attribute groups in the base segment group in the order requested
 */
export interface SegmentResults {
  totalCustomers: number;
  includedCustomers: number;
  excludedCustomers: number;
  perAttributeGroup: {
    include: number[];
    exclude: number[];
  };
}

/**
 * Full segment details returned from the segments/{segment} endpoint
 */
export interface SegmentDetailBase {
  id: number;
  name: string;
  clientId: number;
  clientName: string;
  description: string;
  totalCustomers: number;
  includedCustomers: number;
  excludedCustomers: number;
  createdById: string;
  createdByName: string;
  createdAt: string;
  lastModifiedDate: string;
  isEditable: boolean;
  isShared: boolean;
  lastPushDate?: string;
  isDependency: boolean;
}

export interface SegmentDetailResponseAttribute {
  attributeId: number;
  name: string;
  columnName: string;
  filterType: SegmentAttributeFilterType;
  /** JSON string */
  selectedFilters: string;
  // calculated customer count only provided for attributes in the top level group
  customers?: number;
  filterOperator: SegmentFilterOperator;
  isTransactional?: boolean;
}

export interface SegmentDetailResponseAttributeGroup {
  operator: SegmentAttributeOperator;
  predicates: (SegmentDetailResponseAttribute | SegmentDetailResponseAttributeGroup)[];
  // calculated customer count only provided for groups in the top level group
  customers?: number;
}

export interface SegmentDetailResponseAttributeGroupPair {
  include: SegmentDetailResponseAttributeGroup;
  exclude: SegmentDetailResponseAttributeGroup;
}

export interface SegmentDetailResponse extends SegmentDetailBase {
  attributes: SegmentDetailResponseAttributeGroupPair;
}

interface SegmentDetailAttributeBase {
  attributeId: number;
  name: string;
  columnName: string;
  filterType: SegmentAttributeFilterType;
  customers?: number;
  filterOperator: SegmentFilterOperator;
  isTransactional?: boolean;
}

export interface SegmentDetailAttributeOptions extends SegmentDetailAttributeBase {
  filterType: 'options';
  selectedFilters: string[] | string;
  isTransactional: false;
}

export interface SegmentDetailAttributeRange extends SegmentDetailAttributeBase {
  filterType: 'range';
  selectedFilters: {
    min: number;
    max: number;
  };
  isTransactional: false;
}

export interface SegmentDetailAttributeDate extends SegmentDetailAttributeBase {
  filterType: 'datetime';
  selectedFilters:
    | {
        min: string;
        max: string;
      }
    | number;
  isTransactional: false;
}

export interface SegmentDetailAttributeTransactional extends SegmentDetailAttributeBase {
  isTransactional: true;
  selectedTransactionalFilters: {
    operator: SegmentAttributeOperator;
    predicates: SelectedAttribute[];
  };
  groupBy?: GroupByFilter;
}

export interface SegmentDetailParsedAttributeGroup {
  operator?: SegmentAttributeOperator;
  predicates?: Array<
    | SegmentDetailAttributeOptions
    | SegmentDetailAttributeRange
    | SegmentDetailAttributeDate
    | SegmentDetailParsedAttributeGroup
  >;
  customers?: number;
  transactionalFilters?: {
    operator: SegmentAttributeOperator;
    predicates: SelectedAttribute[];
  };
}

export interface SegmentDetailParsedAttributeGroupPair {
  include: SegmentDetailParsedAttributeGroup;
  exclude: SegmentDetailParsedAttributeGroup;
}

/**
 * Result after parsing the filters from SegmentDetailResponse JSON strings
 */
export interface SegmentDetailResponseParsed extends SegmentDetailBase {
  attributes: SegmentDetailParsedAttributeGroupPair;
  selectedAttributes: SegmentAttributeGroupPair;
  results: SegmentResults;
  composition?: SegmentComposition;
}

export interface PushDestination {
  displayName: string;
  pushDestinationId: number;
  supportsAutomaticPushes?: boolean;
}

export interface PushCampaignOverview {
  displayNames: string[];
  isDescending: boolean;
  items: PushCampaignItems;
  propertyNames: string[];
  sortBy: string;
}

export interface PushCampaignItems {
  results: any[];
  totalItems: number;
}

export interface PushCampaign {
  campaignId: string;
  campaignName: string;
  campaignStatus: string;
  createdBy: string;
  dateCreadted: string;
}

export interface PushHistory {
  campaignId: string;
  campaignName: string;
  customersPushed: number;
  dateLastRun: string;
  details: PushHistoryDetails;
  isOneTime: boolean;
  automaticPushStatus?: number;
  oneTimePushStatus?: number;
  pushId: number;
  targetSystemId: number;
  targetSystemName: string;
  totalCustomers: number;
}

interface PushHistoryDetails {
  numberRecordsProcessed?: number;
  numberRecordsFailed?: number;
  unprocessedRecords?: number;
}

export interface PushHistoryOverview {
  results: PushHistory[];
  totalItems: number;
}

interface PushRun {
  runId: number;
  dateCreated: string;
  status: number;
  totalCustomers: number;
  customersPushed: number;
  details: string;
}

export interface PushOverview {
  campaignId: string;
  pushDestinationName: string;
  pushDestinationId: number;
  pushRecordId: number;
  status: PushStatus;
  runs: RunsOverview;
}

interface RunsOverview {
  results: PushRun[];
  totalItems: number;
}

export interface AvailableCompositions {
  segmentId: number;
  segmentName: string;
  totalCustomers: number;
}

export interface SegmentComposition {
  include?: SegmentCompositionSelection;
  exclude?: SegmentCompositionSelection;
}

export interface SegmentCompositionSelection {
  segmentId?: number;
  segmentOperator?: number;
  customers?: number;
  segmentName?: string;
}

export interface SegmentDependencyOverview {
  results: SegmentDependency[];
  totalItems: number;
}

interface SegmentDependency {
  createdById: string;
  createdByName: string;
  description: string;
  id: number;
  lastModifiedDate: string;
  name: string;
  totalCustomers: number;
}

export interface GroupByFilter {
  groupByFilterId: number;
  groupByFilterColumnName: string;
  groupByFilterName: string;
  groupByValues: GroupByValues;
}

export interface GroupByValues {
  groupByOperator: SegmentAttributeOperator;
  groupByPredicates: GroupByPredicates[];
}

export interface GroupByPredicates {
  valueFilterId: number;
  valueFilterColumnName: string;
  valueFilterName: string;
  aggregateFunction: number;
  aggregateOperator: number;
  value: string;
}

export enum GroupByAggregateFunction {
  'Sum' = 0,
  'Average' = 1,
  'Count' = 2,
  'Count Distinct' = 3,
  'Minimum' = 4,
  'Maximum' = 5
}

export enum GroupByOperator {
  'Is Greater Than' = 0,
  'Is Less Than' = 1,
  'Is Equal To' = 2
}
